import { FormControlLabel, Grid, MenuItem, Switch, TextField } from "@mui/material";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTenantService, TenantLifecycleStatus, toTenantLifecycleStatus, tenantLifecycleStatusToNumber } from "./tenantService";
import { useHistory } from "react-router-dom";
import { useState } from "react";
import { FormContainer, FormHeader, FormSection } from "../shared/FormLayout";

type Props = {
  tenant: any;
};

export const EditTenantForm: React.FC<Props> = ({ tenant }) => {

  const isAdd = !tenant;

  const [includeTypes, setIncludeTypes] = useState<boolean>(true);

  const tenantService = useTenantService();
  const history = useHistory();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Navn er påkrevd"),
  });
  const formOptions = {
    defaultValues: {
      name: tenant?.name ?? "",
      description: tenant?.description ?? "",
      notes: tenant?.notes ?? "",
      lifecycleStatus: toTenantLifecycleStatus(tenant?.lifecycleStatus),
      subscriptionPriceSite: tenant?.subscriptionPrices?.site ?? 13000,
      subscriptionPriceEntity: tenant?.subscriptionPrices?.entity ?? 2000,
    },
    resolver: yupResolver(validationSchema),
  };

  const { register, handleSubmit, formState, setValue } = useForm(formOptions);
  const { errors, isSubmitting } = formState;

  const onSubmit = async (values: any) => {
    const { subscriptionPriceSite, subscriptionPriceEntity, lifecycleStatus, ...rest } = values;
    const formData = {
      ...rest,
      lifecycleStatus: tenantLifecycleStatusToNumber(lifecycleStatus),
      subscriptionPrices: {
        site: Number(subscriptionPriceSite),
        entity: Number(subscriptionPriceEntity),
      },
    };
    if(isAdd) {
      await tenantService.post({...formData, createdOn: new Date().toUTCString()}, includeTypes);
    } else {
      await tenantService.put(tenant.id, {...tenant, ...formData});
    }
    history.push('/tenants')
  };

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <FormHeader section="Tenants" title={tenant?.name || "Ny tenant"} isSubmitting={isSubmitting}>
        {isAdd && (
          <FormControlLabel
            control={
              <Switch
                checked={includeTypes}
                onChange={(_, v) => setIncludeTypes(v)}
                color="primary"
              />
            }
            label="Inkluder entitetstyper"
          />
        )}
      </FormHeader>

      <FormSection title="Grunninfo">
        <Grid item xs={12} md={8}>
          <TextField
            label="Navn"
            size="small"
            fullWidth
            {...register("name")}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            {...register("lifecycleStatus")}
            size="small"
            fullWidth
            select
            onChange={(e) => setValue("lifecycleStatus", e.target.value)}
            label="Status"
            defaultValue={formOptions.defaultValues.lifecycleStatus}
          >
            {Object.values(TenantLifecycleStatus).map((status) => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField label="Beskrivelse" size="small" fullWidth multiline rows={2} {...register("description")} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Notater" size="small" fullWidth multiline rows={3} {...register("notes")} />
        </Grid>
      </FormSection>

      <FormSection title="Abonnementspriser">
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Standard sitepris (NOK)"
            size="small"
            type="text"
            fullWidth
            inputProps={{ inputMode: "numeric" }}
            {...register("subscriptionPriceSite")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Standard entitypris (NOK)"
            size="small"
            type="text"
            fullWidth
            inputProps={{ inputMode: "numeric" }}
            {...register("subscriptionPriceEntity")}
          />
        </Grid>
      </FormSection>
    </FormContainer>
  );
};
